import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Fullscreen as FullscreenIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  QrCode2 as QrIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { marketingLogos } from '../../content/marketing';
import {
  buildPosterFilename,
  canvasToPreviewDataUrl,
  displayJoinUrl,
  downloadCanvasAsPng,
  downloadPosterAsPdf,
  renderJoinPoster,
  renderQrOnly,
} from '../../utils/joinQrPoster';

const JoinQrDialog = ({ open, link, branding, onClose, onCopyLink }) => {
  const [includeUniversityBranding, setIncludeUniversityBranding] = useState(true);
  const [preview, setPreview] = useState({ status: 'idle', url: '', error: '' });
  const [busyAction, setBusyAction] = useState('');
  const [presenting, setPresenting] = useState(false);
  const [presentQrUrl, setPresentQrUrl] = useState('');
  const posterRef = useRef(null);

  const universityName = branding?.name || '';
  const joinUrl = link?.joinUrl || '';

  const buildPoster = useCallback(() => renderJoinPoster({
    joinUrl,
    universityName,
    label: link?.label,
    expiresAt: link?.expiresAt,
    allowedDomains: link?.allowedDomains || [],
    includeUniversityBranding,
    branding,
  }), [joinUrl, universityName, link, includeUniversityBranding, branding]);

  useEffect(() => {
    if (!open || !joinUrl) {
      return undefined;
    }

    let cancelled = false;
    posterRef.current = null;
    setPreview((prev) => ({ ...prev, status: 'loading', error: '' }));
    buildPoster()
      .then((poster) => {
        if (cancelled) {
          return;
        }
        posterRef.current = poster;
        setPreview({ status: 'ready', url: canvasToPreviewDataUrl(poster.canvas), error: '' });
      })
      .catch((error) => {
        if (!cancelled) {
          setPreview({ status: 'error', url: '', error: error.message || 'Unable to draw the QR poster.' });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, joinUrl, buildPoster]);

  useEffect(() => {
    if (!presenting || !joinUrl) {
      return undefined;
    }
    let cancelled = false;
    renderQrOnly({
      joinUrl,
      accentColor: includeUniversityBranding ? branding?.colors?.primary : null,
      size: 1400,
    }).then((canvas) => {
      if (!cancelled) {
        setPresentQrUrl(canvas.toDataURL('image/png'));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [presenting, joinUrl, includeUniversityBranding, branding]);

  const runAction = async (action, handler) => {
    setBusyAction(action);
    try {
      await handler();
    } catch (error) {
      setPreview((prev) => ({ ...prev, error: error.message || 'Download failed. Please try again.' }));
    } finally {
      setBusyAction('');
    }
  };

  const ensurePoster = async () => {
    if (posterRef.current) {
      return posterRef.current;
    }
    const poster = await buildPoster();
    posterRef.current = poster;
    return poster;
  };

  const handleDownloadPdf = () => runAction('pdf', async () => {
    const poster = await ensurePoster();
    await downloadPosterAsPdf({
      ...poster,
      joinUrl,
      title: `Join ${universityName || 'Virtual Medic'} – ${link.label}`,
      filename: buildPosterFilename(universityName, link.label, 'pdf'),
    });
  });

  const handleDownloadPng = () => runAction('png', async () => {
    const poster = await ensurePoster();
    await downloadCanvasAsPng(poster.canvas, buildPosterFilename(universityName, link.label, 'png'));
  });

  const handleDownloadQrOnly = () => runAction('qr', async () => {
    const canvas = await renderQrOnly({
      joinUrl,
      accentColor: includeUniversityBranding ? branding?.colors?.primary : null,
    });
    await downloadCanvasAsPng(canvas, buildPosterFilename(universityName, link.label, 'png', 'qr-code'));
  });

  const handleBrandingToggle = (event) => {
    setIncludeUniversityBranding(event.target.checked);
  };

  if (!link) {
    return null;
  }

  const brandingHint = branding?.logoDataUrl
    ? 'Adds your university logo and colour alongside Virtual Medic branding.'
    : 'Adds your university name and colour. Upload a logo in University settings to include it too.';

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="join-qr-dialog-title">
        <DialogTitle id="join-qr-dialog-title" sx={{ pr: 6 }}>
          {link.label}
          <Typography variant="body2" color="text.secondary">
            {`QR sign-up code · valid until ${dayjs(link.expiresAt).format('D MMM YYYY')}`}
          </Typography>
          <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
            <Box
              sx={{
                flex: '0 0 auto',
                width: { xs: '100%', md: 330 },
                maxWidth: 360,
                mx: 'auto',
                aspectRatio: '210 / 297',
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 2,
              }}
            >
              {preview.status === 'ready' && preview.url ? (
                <Box
                  component="img"
                  src={preview.url}
                  alt={`Printable QR code poster for ${link.label}`}
                  sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                />
              ) : preview.status === 'error' ? (
                <Typography variant="body2" color="error" sx={{ p: 2, textAlign: 'center' }}>
                  {preview.error}
                </Typography>
              ) : (
                <CircularProgress size={32} />
              )}
            </Box>

            <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                label="Sign-up link"
                value={joinUrl}
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy link">
                        <IconButton edge="end" aria-label="Copy sign-up link" onClick={() => onCopyLink?.(joinUrl)}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              <Box>
                <FormControlLabel
                  control={<Switch checked={includeUniversityBranding} onChange={handleBrandingToggle} />}
                  label="Include university branding"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5 }}>
                  {brandingHint}
                </Typography>
              </Box>

              <Divider />

              <Stack spacing={1.25}>
                <Typography variant="subtitle2">Download</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={busyAction === 'pdf' ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
                    onClick={handleDownloadPdf}
                    disabled={Boolean(busyAction) || preview.status === 'loading'}
                  >
                    Poster (PDF)
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={busyAction === 'png' ? <CircularProgress size={16} /> : <ImageIcon />}
                    onClick={handleDownloadPng}
                    disabled={Boolean(busyAction) || preview.status === 'loading'}
                  >
                    Poster (PNG)
                  </Button>
                  <Button
                    variant="text"
                    startIcon={busyAction === 'qr' ? <CircularProgress size={16} /> : <QrIcon />}
                    onClick={handleDownloadQrOnly}
                    disabled={Boolean(busyAction)}
                  >
                    QR only (PNG)
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  The A4 poster is print-ready. Use "QR only" to drop the code into lecture slides.
                </Typography>
              </Stack>

              <Button
                variant="outlined"
                color="secondary"
                startIcon={<FullscreenIcon />}
                onClick={() => setPresenting(true)}
                sx={{ alignSelf: 'flex-start' }}
              >
                Present full screen
              </Button>

              <Alert severity="info" variant="outlined">
                Anyone with this code can create an account in your university. Students must verify their email
                before signing in. Pause, replace, or revoke the code if it is shared beyond your cohort.
              </Alert>
              {preview.status !== 'error' && preview.error && <Alert severity="error">{preview.error}</Alert>}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog fullScreen open={presenting} onClose={() => setPresenting(false)} aria-label="Presenting QR code">
        <Box
          sx={{
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 2, md: 3 },
            p: { xs: 2, md: 4 },
            pt: { xs: 9, md: 4 },
            background: 'linear-gradient(135deg, #0d2d4e 0%, #1c6da1 100%)',
            color: '#fff',
            textAlign: 'center',
            position: 'relative',
          }}
        >
          <Box
            component="img"
            src={marketingLogos.lockupLight}
            alt="Virtual Medic"
            sx={{ position: 'absolute', top: { xs: 16, md: 24 }, left: { xs: 16, md: 32 }, height: { xs: 44, md: 72 } }}
          />
          <IconButton
            aria-label="Exit full screen"
            onClick={() => setPresenting(false)}
            sx={{ position: 'absolute', top: 16, right: 16, color: '#fff' }}
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ fontFamily: '"Sora", "Manrope", sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2.6rem' }, lineHeight: 1.15 }}>
            {universityName ? `Scan to join ${universityName}` : 'Scan to join Virtual Medic'}
          </Typography>
          <Typography sx={{ opacity: 0.85, fontSize: { xs: '1rem', md: '1.35rem' }, fontWeight: 600 }}>
            {link.label}
          </Typography>
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: 4,
              p: { xs: 1.5, md: 2.5 },
              width: 'min(68vh, 82vw)',
              aspectRatio: '1 / 1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
            }}
          >
            {presentQrUrl ? (
              <Box component="img" src={presentQrUrl} alt="Sign-up QR code" sx={{ width: '100%', height: '100%' }} />
            ) : (
              <CircularProgress />
            )}
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', md: '1.25rem' }, wordBreak: 'break-all', opacity: 0.92 }}>
            {displayJoinUrl(joinUrl)}
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export default JoinQrDialog;
